import { Currency, CurrencyAmount, Native, Token } from '@pancakeswap/sdk'
import { useBalance, useReadContracts } from '@pancakeswap/wagmi'
import { useAllTokens } from 'hooks/Tokens'
import { useActiveChainId } from 'hooks/useActiveChainId'
import useNativeCurrency from 'hooks/useNativeCurrency'
import orderBy from 'lodash/orderBy'
import { useMemo } from 'react'
import { safeGetAddress } from 'utils'
import { Address, erc20Abi, getAddress, isAddress } from 'viem'
import { useAccount } from 'wagmi'

/**
 * Returns a map of the given addresses to their native currency balances.
 */
export function useNativeBalances(uncheckedAddresses?: (string | undefined)[]): {
  [address: string]: CurrencyAmount<Native> | undefined
} {
  const native = useNativeCurrency()
  const { chainId } = useActiveChainId()

  const addresses: Address[] = useMemo(
    () =>
      uncheckedAddresses
        ? orderBy(uncheckedAddresses.map(safeGetAddress).filter((a): a is Address => a !== undefined))
        : [],
    [uncheckedAddresses],
  )

  const { data, isFetched } = useBalance({
    address: addresses[0],
    chainId,
    query: {
      enabled: addresses.length > 0 && Boolean(addresses[0]),
    },
  })

  return useMemo(() => {
    if (!addresses.length || !isFetched) {
      return {}
    }

    const balance = data?.value ?? 0n

    return addresses.reduce<{ [address: string]: CurrencyAmount<Native> }>((memo, address) => {
      const amount = CurrencyAmount.fromRawAmount(native, balance)
      memo[address] = amount
      const checksummed = getAddress(address)
      if (checksummed !== address) {
        memo[checksummed] = amount
      }
      return memo
    }, {})
  }, [addresses, data, isFetched, native])
}

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[],
): [{ [tokenAddress: string]: CurrencyAmount<Token> | undefined }, boolean] {
  const { chainId } = useActiveChainId()

  const validatedTokens: Token[] = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address || '')) ?? [],
    [tokens],
  )

  const contracts = useMemo(
    () =>
      validatedTokens.map((token) => ({
        address: token.address,
        abi: erc20Abi,
        functionName: 'balanceOf' as const,
        args: [address as Address] as const,
        chainId,
      })),
    [validatedTokens, address, chainId],
  )

  const { data, isFetched, isLoading } = useReadContracts({
    contracts,
    query: {
      enabled: Boolean(address && validatedTokens.length > 0),
    },
  })

  return [
    useMemo(
      () =>
        address && validatedTokens.length > 0
          ? validatedTokens.reduce<{ [tokenAddress: string]: CurrencyAmount<Token> | undefined }>((memo, token, i) => {
              const value = data?.[i]?.result
              if (typeof value !== 'undefined') {
                memo[token.address] = CurrencyAmount.fromRawAmount(token, BigInt(value.toString()))
              } else if (isFetched) {
                memo[token.address] = CurrencyAmount.fromRawAmount(token, 0n)
              }
              return memo
            }, {})
          : {},
      [address, validatedTokens, data, isFetched],
    ),
    Boolean(address && validatedTokens.length > 0 && !isFetched && isLoading),
  ]
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[],
): { [tokenAddress: string]: CurrencyAmount<Token> | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): CurrencyAmount<Token> | undefined {
  const tokenBalances = useTokenBalances(
    account,
    useMemo(() => [token], [token]),
  )
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined | null)[],
): (CurrencyAmount<Currency> | undefined)[] {
  const tokens = useMemo(
    () => currencies?.filter((currency): currency is Token => Boolean(currency?.isToken)) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...(currencies ?? [])],
  )

  const tokenBalances = useTokenBalances(account, tokens)
  const containsNative: boolean = useMemo(
    () => currencies?.some((currency) => currency?.isNative) ?? false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...(currencies ?? [])],
  )
  const uncheckedAddresses = useMemo(() => (containsNative ? [account] : []), [containsNative, account])
  const nativeBalance = useNativeBalances(uncheckedAddresses)

  return useMemo(
    () =>
      currencies?.map((currency) => {
        if (!account || !currency) return undefined
        if (currency?.isToken) return tokenBalances[currency.address]
        if (currency?.isNative) return nativeBalance[account] || nativeBalance[getAddress(account)]
        return undefined
      }) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, ...(currencies ?? []), nativeBalance, tokenBalances],
  )
}

export function useCurrencyBalance(account?: string, currency?: Currency | null): CurrencyAmount<Currency> | undefined {
  return useCurrencyBalances(
    account,
    useMemo(() => [currency], [currency]),
  )[0]
}

// mimics useAllBalances
export function useAllTokenBalances(): { [tokenAddress: string]: CurrencyAmount<Token> | undefined } {
  const { address: account } = useAccount()
  const allTokens = useAllTokens()
  const allTokensArray = useMemo(() => Object.values(allTokens ?? {}), [allTokens])
  const balances = useTokenBalances(account ?? undefined, allTokensArray)
  return balances ?? {}
}
